import { useEffect, useRef, useState } from "react";

type Handling = {
  doNotDisturb: boolean;
  ringSeconds: number;
  forwardMode: "off" | "always" | "busy" | "unanswered";
  forwardTo: string;
  callWaiting: boolean;
  voicemailEnabled: boolean;
};
type Applied = {
  lineId: string;
  deviceId: string;
  connectionVersion: number;
  revision: number;
  settings: Handling;
  appliedAt: string | null;
  configurationMatched: boolean;
  supported: string[];
};
type Api = <T>(
  path: string,
  options?: {
    method?: string;
    body?: unknown;
    csrf?: string | undefined;
    signal?: AbortSignal;
  },
) => Promise<T>;
export const handlingMatches = (
  saved: Partial<Handling> | null,
  live: Handling,
) =>
  saved !== null &&
  (Object.keys(live) as (keyof Handling)[]).every(
    (key) => saved[key] === live[key],
  );

export function CallHandlingStatus({
  value,
  saved,
  savedVersion,
  busy,
  readOnly,
  onApply,
}: {
  value: Applied | undefined;
  saved: Partial<Handling> | null;
  savedVersion: number;
  busy: boolean;
  readOnly: boolean;
  onApply: () => void;
}) {
  if (!value)
    return (
      <p>
        Live call handling is not connected for this line. Staff must install
        its assigned phone on the voice server first.
      </p>
    );
  const matched =
    value.configurationMatched &&
    value.appliedAt &&
    handlingMatches(saved, value.settings);
  return (
    <div>
      <h3>Call handling on the phone server</h3>
      <p>
        {value.configurationMatched
          ? matched
            ? "Your saved call handling is applied."
            : "The server settings below are active. Save your changes, then apply them here."
          : "The equipment assignment or server configuration changed. Staff must reconcile it before another change can be applied."}
      </p>
      <dl className="cr-details">
        <dt>Do Not Disturb</dt>
        <dd>{value.settings.doNotDisturb ? "On" : "Off"}</dd>
        <dt>Ring time</dt>
        <dd>{value.settings.ringSeconds} seconds</dd>
        <dt>Forwarding</dt>
        <dd>
          {value.settings.forwardMode === "off"
            ? "Off"
            : `${value.settings.forwardMode === "always" ? "All calls" : value.settings.forwardMode === "busy" ? "When busy" : "When unanswered"}: ${value.settings.forwardTo}`}
        </dd>
        <dt>Second incoming call</dt>
        <dd>
          {value.settings.callWaiting
            ? "Allowed"
            : "Busy while this line is in use"}
        </dd>
        <dt>Voicemail</dt>
        <dd>
          {value.settings.voicemailEnabled
            ? "On for busy or unanswered calls"
            : "Off"}
        </dd>
        {value.appliedAt && (
          <>
            <dt>Last applied</dt>
            <dd>{new Date(value.appliedAt).toLocaleString()}</dd>
          </>
        )}
      </dl>
      <p>
        Applying changes affects new incoming calls. It does not interrupt an
        existing call or change emergency dialing. Call waiting tones and
        switching between calls also depend on your phone.
      </p>
      <p>
        3-way calling and Caller ID requests are separate; this action does not
        activate them.
      </p>
      {!savedVersion && (
        <p>Save the feature settings below before applying call handling.</p>
      )}
      {!readOnly && (
        <button
          className="cr-button"
          type="button"
          disabled={
            busy ||
            !value.configurationMatched ||
            !savedVersion ||
            Boolean(matched)
          }
          onClick={onApply}
        >
          {busy
            ? "Applying call handling…"
            : matched
              ? "Call handling applied"
              : "Apply saved call handling"}
        </button>
      )}
    </div>
  );
}

export function LiveCallHandling({
  api,
  accountId,
  customer,
  csrf,
  lineId,
  savedVersion,
  saved,
  readOnly,
}: {
  api: Api;
  accountId: string;
  customer: boolean;
  csrf: string | undefined;
  lineId: string;
  savedVersion: number;
  saved: Partial<Handling> | null;
  readOnly: boolean;
}) {
  const base = `/${customer ? "customer" : "admin"}/accounts/${accountId}/line-controls`;
  const [state, setState] = useState<{ items: Applied[] } | null>(null);
  const [error, setError] = useState(""),
    [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false),
    [refresh, setRefresh] = useState(0);
  const working = useRef(false),
    command = useRef({ fingerprint: "", key: "" });
  useEffect(() => {
    const abort = new AbortController();
    const timeout = setTimeout(() => {
      setError(
        "The phone server did not respond. Refresh to check whether a previous change was applied.",
      );
      abort.abort();
    }, 18_000);
    setState(null);
    setError("");
    void api<{ items: Applied[] }>(base, { signal: abort.signal })
      .then((value) => {
        if (!abort.signal.aborted) setState(value);
      })
      .catch((cause: Error) => {
        if (!abort.signal.aborted) setError(cause.message);
      })
      .finally(() => clearTimeout(timeout));
    return () => {
      clearTimeout(timeout);
      abort.abort();
    };
  }, [api, base, lineId, savedVersion, refresh]);
  const value = state?.items.find((item) => item.lineId === lineId);
  return (
    <section aria-label="Live call handling">
      {notice && (
        <p className="cr-notice" role="status">
          {notice}
        </p>
      )}
      {error && (
        <p className="cr-notice error" role="alert">
          {error}
        </p>
      )}
      {!state && !error && (
        <p role="status">Checking call handling on the phone server…</p>
      )}
      {state && (
        <CallHandlingStatus
          value={value}
          saved={saved}
          savedVersion={savedVersion}
          busy={busy}
          readOnly={readOnly}
          onApply={() => {
            if (!value || working.current || readOnly) return;
            working.current = true;
            setBusy(true);
            setError("");
            setNotice("");
            const body = {
              lineId,
              expectedFeatureVersion: savedVersion,
              expectedConnectionVersion: value.connectionVersion,
              expectedRevision: value.revision,
            };
            const fingerprint = JSON.stringify(body);
            if (fingerprint !== command.current.fingerprint)
              command.current = { fingerprint, key: crypto.randomUUID() };
            void api<Applied>(`${base}/apply`, {
              method: "POST",
              csrf,
              body: { ...body, requestKey: command.current.key },
            })
              .then((applied) => {
                setState({ items: [applied] });
                setNotice(
                  "Call handling applied to the phone server. Other feature requests have not changed.",
                );
                command.current = { fingerprint: "", key: "" };
              })
              .catch((cause: Error) => {
                setError(
                  `${cause.message} Refresh server status before retrying.`,
                );
              })
              .finally(() => {
                working.current = false;
                setBusy(false);
              });
          }}
        />
      )}
      {(state || error) && (
        <button
          type="button"
          className="cr-button secondary"
          disabled={busy}
          onClick={() => setRefresh((current) => current + 1)}
        >
          Refresh server status
        </button>
      )}
    </section>
  );
}
